import React, { useMemo, useState } from "react";
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  BoltIcon,
  BoltSlashIcon,
  ClockIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import { useScreenTimeline } from "../../hooks/useReports";
import { reportService } from "../../api/services/reportService";
import {
  ReportFormat,
  ScreenTimelineSegment,
} from "../../schemas/reportSchema";
import Loader from "../common/Loader";
import { formatDisplayDateTime, toDisplayDateInput } from "../../utils/dateUtils";

interface ScreenTimelineProps {
  screenId: string;
  /** Gates the query so it only fires while the tab is actually visible. */
  isActive?: boolean;
  /** Shown as a caption so the tab identifies the screen on its own. */
  screenName?: string;
  city?: string;
  region?: string;
}

// "2d 5h" / "4h 40m" / "35m" — days and minutes are never shown together,
// since minute precision is noise once an outage has run for days.
const formatDuration = (minutes: number): string => {
  if (minutes < 1) return "<1m";
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (mins > 0 && days === 0) parts.push(`${mins}m`);
  return parts.join(" ") || "<1m";
};

// "15 Jul 2026, 14:30" — 24-hour, matching the generated report files. Shared
// with the exporter's formatter (and pinned to the same timezone) so the panel
// and the downloaded file can't drift apart.
const formatDateTime = formatDisplayDateTime;

const toDateInput = toDisplayDateInput;

export default function ScreenTimeline({
  screenId,
  isActive = true,
  screenName,
  city,
  region,
}: ScreenTimelineProps) {
  const [startDate, setStartDate] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return toDateInput(d);
  });
  const [endDate, setEndDate] = useState<string>(() => toDateInput(new Date()));
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // "Bandra, Mumbai" — reads as an address, and collapses cleanly when only
  // one of the two is set.
  const locationLabel = [region, city].filter(Boolean).join(", ");

  const { timeline, isLoading, error, refetch } = useScreenTimeline(
    screenId,
    { startDate, endDate },
    isActive,
  );

  const segments = timeline?.segments ?? [];

  // Proportional widths for the summary bar. Guard the divisor so a
  // zero-length window can't produce NaN flex values.
  const totalMinutes = useMemo(
    () => segments.reduce((sum, s) => sum + s.durationMinutes, 0),
    [segments],
  );

  // The file is regenerated server-side from the same window the panel is
  // showing, so it can't drift from what the user is looking at.
  const handleDownload = async (format: ReportFormat) => {
    if (segments.length === 0) {
      toast.error("No timeline data available to download");
      return;
    }

    setIsDownloading(true);
    try {
      toast.info(`Generating ${format} timeline...`);

      const downloadResult = await reportService.downloadScreenStatusTimeline(
        screenId,
        { startDate, endDate, format },
      );

      const byteCharacters = atob(downloadResult.base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: downloadResult.mimeType });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = downloadResult.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Timeline downloaded successfully");
    } catch (error) {
      console.error("Error downloading timeline:", error);
      toast.error("Failed to download timeline");
    } finally {
      setIsDownloading(false);
    }
  };

  const renderBar = () => (
    <div className="flex h-3 w-full overflow-hidden rounded-full bg-gray-200">
      {segments.map((segment, index) => (
        <div
          key={`${segment.startTime}-${index}`}
          className={segment.status === "ON" ? "bg-green-500" : "bg-red-500"}
          style={{
            flexGrow: segment.durationMinutes,
            flexBasis: 0,
            // Keep brief blips visible instead of collapsing them to nothing.
            minWidth: "2px",
          }}
          title={`${segment.status} · ${formatDateTime(
            segment.startTime,
          )} → ${formatDuration(segment.durationMinutes)}`}
        />
      ))}
    </div>
  );

  const renderSummary = () => {
    if (!timeline) return null;
    const cards = [
      {
        label: "Uptime",
        value: `${timeline.uptimePercentage}%`,
        tone: "text-green-600",
      },
      {
        label: "Total ON",
        value: formatDuration(timeline.totalOnMinutes),
        tone: "text-gray-800",
      },
      {
        label: "Total OFF",
        value: formatDuration(timeline.totalOffMinutes),
        tone: "text-red-600",
      },
      {
        label: "Outages",
        value: String(timeline.offCount),
        tone: "text-gray-800",
      },
    ];
    return (
      <div className="grid flex-shrink-0 grid-cols-2 sm:grid-cols-4 gap-3">
        {cards.map((card) => (
          <div
            key={card.label}
            className="border rounded-lg px-3 py-2 bg-gray-50"
          >
            <div className="text-[11px] uppercase tracking-wide text-gray-500">
              {card.label}
            </div>
            <div className={`text-lg font-semibold ${card.tone}`}>
              {card.value}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Rendered newest-first (the bar above stays chronological), but the label
  // logic keys off the chronological index, not the display position.
  const events = useMemo(
    () => segments.map((segment, index) => ({ segment, index })).reverse(),
    [segments],
  );

  const renderEvent = (segment: ScreenTimelineSegment, index: number) => {
    const isOn = segment.status === "ON";
    // The earliest segment starts because the window starts, not because the
    // screen changed state — calling that a transition would be a lie.
    const isWindowOpening = index === 0;
    const label = isWindowOpening
      ? isOn
        ? "Online at start of period"
        : "Offline at start of period"
      : isOn
        ? "Came back ON"
        : "Went OFF";

    return (
      <li
        key={`${segment.startTime}-${index}`}
        className="relative pl-8 pb-5 last:pb-0"
      >
        <span
          className={`absolute left-[9px] top-1.5 z-10 h-3 w-3 rounded-full ring-4 ring-white ${
            isOn ? "bg-green-500" : "bg-red-500"
          }`}
        />
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`text-sm font-semibold ${
              isOn ? "text-green-700" : "text-red-700"
            }`}
          >
            {label}
          </span>
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-600">
          <span>{formatDateTime(segment.startTime)}</span>
          <span className="flex items-center gap-1">
            <ClockIcon className="h-3.5 w-3.5" />
            {formatDuration(segment.durationMinutes)}
            {segment.isOngoing ? " and counting" : ""}
          </span>
        </div>
      </li>
    );
  };

  return (
    // Height comes from the flex chain, not a percentage — the parent is sized
    // by flex-grow, so `h-full` here would resolve to auto and the scroll
    // container below would never get a bounded height.
    <div className="flex min-h-0 flex-1 flex-col">
      {/* The tab is opened straight from a screen row, so it has to say which
          screen these numbers belong to. */}
      {(screenName || locationLabel) && (
        <div className="mb-3 flex flex-shrink-0 flex-wrap items-center gap-x-3 gap-y-1">
          {screenName && (
            <span className="text-md font-semibold text-gray-800">
              {screenName}
            </span>
          )}
          {locationLabel && (
            <span className="flex items-center gap-1 text-sm text-gray-500">
              <MapPinIcon className="h-4 w-4" />
              {locationLabel}
            </span>
          )}
        </div>
      )}

      {/* Controls */}
      <div className="mb-4 flex flex-shrink-0 flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-col">
            <label className="mb-1 text-xs text-gray-500">From</label>
            <input
              type="date"
              value={startDate}
              max={endDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex flex-col">
            <label className="mb-1 text-xs text-gray-500">To</label>
            <input
              type="date"
              value={endDate}
              min={startDate}
              max={toDateInput(new Date())}
              onChange={(e) => setEndDate(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          {timeline && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Currently</span>
              <span
                className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
                  timeline.currentStatus === "ON"
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-700"
                }`}
              >
                {timeline.currentStatus === "ON" ? (
                  <BoltIcon className="h-3.5 w-3.5" />
                ) : (
                  <BoltSlashIcon className="h-3.5 w-3.5" />
                )}
                {timeline.currentStatus}
              </span>
            </div>
          )}

          <div className="relative">
            <button
              type="button"
              className="rounded bg-primary-default p-2 text-white hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-50"
              title="Download Timeline"
              disabled={segments.length === 0 || isDownloading}
              onClick={() => setShowDownloadMenu(!showDownloadMenu)}
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
            </button>
            {showDownloadMenu && (
              <div className="absolute right-0 z-50 mt-1 w-36 rounded-lg border border-gray-200 bg-white shadow-lg">
                <button
                  type="button"
                  className="w-full rounded-t-lg px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                  onClick={() => {
                    setShowDownloadMenu(false);
                    handleDownload("PDF");
                  }}
                >
                  Download PDF
                </button>
                <button
                  type="button"
                  className="w-full rounded-b-lg px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                  onClick={() => {
                    setShowDownloadMenu(false);
                    handleDownload("EXCEL");
                  }}
                >
                  Download Excel
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-1 items-center justify-center py-10">
          <Loader size="small" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          Failed to load timeline: {error.message}
        </div>
      ) : segments.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center py-10 text-gray-500">
          <ClockIcon className="mb-2 h-10 w-10 text-gray-300" />
          <p className="text-sm">No status history for this period</p>
        </div>
      ) : (
        <>
          {renderSummary()}

          <div className="mt-4 flex-shrink-0">
            {renderBar()}
            <div className="mt-1 flex justify-between text-[11px] text-gray-500">
              <span>{formatDateTime(timeline!.rangeStart)}</span>
              <span>{formatDateTime(timeline!.rangeEnd)}</span>
            </div>
          </div>

          <div className="mt-4 flex flex-shrink-0 items-center justify-between border-b pb-2">
            <span className="text-xs font-medium text-gray-700">
              Status changes
            </span>
            <span className="text-[11px] text-gray-400">Newest first</span>
          </div>

          {/* Newest-first, so the current state and any ongoing outage are the
              first thing visible without scrolling. */}
          <div className="mt-3 min-h-0 flex-1 overflow-y-auto pr-1">
            <ul className="relative">
              <span className="absolute bottom-2 left-[14px] top-2 w-px bg-gray-200" />
              {events.map(({ segment, index }) => renderEvent(segment, index))}
            </ul>
            {totalMinutes > 0 && (
              <p className="mt-2 pl-8 text-[11px] text-gray-400">
                {segments.length} state change
                {segments.length === 1 ? "" : "s"} over{" "}
                {formatDuration(totalMinutes)}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
